import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { AVAILABILITY } from './flagDisplay';

/**
 * Kills, suspends, or restores a flag.
 *
 * Deliberately separate from the release dialog. This outranks release status entirely —
 * one save takes the feature from everyone regardless of who it was released to — and
 * during an incident nobody should have to think about rollout percentages to switch
 * something off.
 */
const AvailabilityModal = ({ open, node, onClose, onSubmit, submitting }) => {
  const row = (node && node.node && node.node.row) || {};
  const [availability, setAvailability] = useState('LIVE');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    // Open on the *current* state, not the likely next one. Pre-selecting KILLED would
    // mean the form is already "changed" the moment it opens, which defeats the point of
    // only enabling Save on a real edit — and puts a destructive action one stray click away.
    setAvailability(row.availability || 'LIVE');
    setReason(row.availabilityReason || '');
    setMessage(row.availabilityMessage || '');
  }, [open, row.key]);

  if (!node) return null;

  const restoring = availability === 'LIVE';
  const hasChildren = (node.children || []).length > 0;

  // Editing just the overlay copy of an already-suspended flag is a legitimate change, so
  // reason and message count — unlike on the release form, where the reason is only ever
  // metadata about some other change.
  const dirty =
    availability !== (row.availability || 'LIVE') ||
    (!restoring &&
      (reason !== (row.availabilityReason || '') ||
        message !== (row.availabilityMessage || '')));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Availability
        <Typography variant="body2" color="textSecondary" style={{ fontFamily: 'monospace' }}>
          {row.key}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {row.availability !== 'LIVE' && (
          <Box mb={2}>
            <Alert severity="info">
              Currently <strong>{row.availability}</strong>
              {row.availabilityReason ? ` — ${row.availabilityReason}` : ''}
              {row.availabilitySince
                ? ` (since ${new Date(row.availabilitySince).toLocaleString()})`
                : ''}
            </Alert>
          </Box>
        )}

        <Box mb={2}>
          <Typography variant="body2" gutterBottom>
            State
          </Typography>
          <Select
            fullWidth
            variant="outlined"
            margin="dense"
            value={availability}
            onChange={(e) => setAvailability(e.target.value)}>
            {AVAILABILITY.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="textSecondary">
            KILLED is an incident. MAINTENANCE is expected to be temporary and shows an overlay.
            Both switch the feature off for everyone, including anyone on the always-on user list.
          </Typography>
        </Box>

        {!restoring && (
          <>
            <Box mb={2}>
              <TextField
                fullWidth
                variant="outlined"
                label="Reason (internal)"
                helperText="Shown on the row itself, so nobody has to read the audit log mid-incident."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              label="User-facing message"
              helperText="Rendered as the overlay in the app. Plain language, and an ETA if you have one."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </>
        )}

        {restoring && (
          <Alert severity="info">
            Restoring clears the reason, message and attribution. The release log keeps the
            history — a stale “back by 6pm” left on a live flag is worse than showing nothing.
          </Alert>
        )}

        {!restoring && hasChildren && (
          <Box mt={2}>
            <Alert severity="warning">
              This node has <strong>{node.children.length}</strong> child flag(s). They all go
              dark too — that is what a container is for, but check it is what you meant.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          style={{
            backgroundColor: restoring ? '#1e7b34' : '#b3261e',
            color: '#fff',
          }}
          onClick={() =>
            onSubmit(row.key, {
              availability,
              availabilityReason: restoring ? undefined : reason || undefined,
              availabilityMessage: restoring ? undefined : message || undefined,
            })
          }
          disabled={submitting || !dirty}>
          {submitting ? 'Saving…' : restoring ? 'Restore' : `Set ${availability}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailabilityModal;
